/**
 * Color constants for the CAD application.
 * Contains theme-reactive colors using CSS variables and static color values
 * for various UI components including command line, messages, urgent states,
 * queue, location highlights, and notifications.
 *
 * Over time, the idea would be to eliminate use of this file as all colors would come
 * from the arc theming system.
 *
 * Theme-reactive colors are defined in ~/src/colors/colors.css
 *
 */
export const colors = {

    bootstrapBackground: 'var(--cad-colors-bootstrap-background)',
    skeletonBaseColor: 'var(--cad-colors-skeleton-base)',
    skeletonHighlightColor: 'var(--cad-colors-skeleton-highlight)',

    // Command line colors
    commandLineLocationVerified: '#7ED321',
    commandLineLocationUnverified: '#F8E71C',
    commandLineLeafComment: '#CCCCCC',
    commandLineValidationErrorBorder: 'var(--arc-colors-raw-red-600)',
    commandLineHistoryPartialSuccess: '#f5a623',

    // Message colors
    messagesUnreadLineNormal: 'var(--cad-colors-unread-normal)',
    messagesBackground: 'var(--arc-colors-info-content)',

    // Queue colors
    queuePriority: '#d04341',

    // Location colors
    locationHighlightBoxBackground: 'var(--arc-colors-raw-yellow-100)',
    locationHighlightBoxText: '#1E1F20', // this is the same as surface.foreground, but seemed like an incorrect usage; proposed to use use text.primary.inverse

    // Notification colors
    notificationCounterUrgentBackground: '#F8AFA9',
    notificationCounterUrgentText: 'var(--arc-colors-raw-black)',

    // Panel colors
    panelModalBoxShadow: 'rgb(0 0 0 / 75%)',

    // Priority colors
    priorityOneColor: 'var(--arc-colors-raw-red-550)',
    priorityStandardBackground: 'var(--arc-colors-border-default)',

    // Caution marker colors
    cautionMarkerNearby: 'var(--arc-colors-raw-red-550)',
    cautionMarkerPriority1: 'var(--arc-colors-raw-red-550)',
    cautionMarkerPriority2: 'var(--arc-colors-raw-orange-100)',
    cautionMarkerPriority3: 'var(--arc-colors-raw-purple-100)',

    // Map colors - direct values for Esri map API (cannot use CSS variables)
    mapBasemapSwitchBackground: '#000000',
    mapBasemapSwitchText: '#ffffff',
    mapLabelDefault: '#c0c0c0',
    mapLabelAlert: '#f5a623',
    mapLabelText: '#ffffff',
    mapLabelHalo: '#000000',
    mapPolylineBorder: '#bed4dd',
    mapPolylineTracked: '#00C2FF',
    mapPolylineDefault: '#F5F5F5',
    mapLocationPin: '#54A3FF',
    mapClusterLabel: '#1E1F20',
    mapClusterLabelHalo: '#000000',
    mapClusterMarker: 'rgba(255, 255, 255, 0.4)',
    mapClusterHoverBackground: 'rgba(10, 23, 144, 0.7)',
    mapModeButtonDefaultText: 'var(--arc-colors-raw-white)',
    mapModeButtonGradientStart: 'rgb(0 0 0 / 0%)',
    mapModeButtonGradientEnd: 'rgb(0 0 0 / 80%)',
    mapButtonBackground: '#131920',
    mapButtonForeground: 'var(--arc-colors-raw-white)',
    mapButtonTrackingBackground: '#b18234',
    mapButtonTrackingBorder: '#f5a623',

    // Event notification colors
    eventNotificationsFieldIndicatorBackground: '#605542',
    eventNotificationsHighText: 'var(--cad-colors-event-notifications-high-text)',
    eventNotificationsHighBackground: '#FDCD8133',
    eventNotificationsStandardText: 'var(--cad-colors-event-notifications-standard-text)',
    eventNotificationsStandardBackground: '#FDCD8133',

    // DEX colors
    dexResultsImageGradientStart: 'rgb(18 18 18 / 0%)',
    dexResultsImageGradientEnd: 'rgb(18 18 18 / 100%)',
    dexHit: 'var(--arc-colors-raw-red-500)',
    dexNavUrgentBackground: 'var(--cad-colors-dex-nav-urgent-background)',
    dexNavUrgentHover: 'var(--cad-colors-dex-nav-urgent-hover)',
    dexActivityHitBackground: '#61321B',
    dexActivityHitText: 'var(--arc-colors-raw-white)',

    // Subdivision colors
    subdivisionLabel: 'var(--arc-colors-icon-default)',

    // Unit colors
    unitEmergency: 'var(--arc-colors-raw-red-800)',
    unitEmergencyText: 'var(--arc-colors-raw-white)',
    unitTimerIcon: '#ff5a00',
    backupUnitBorder: 'rgb(62, 176, 150)',

    // AED failure colors
    aedFailureHighlighted: 'rgb(109, 14, 85)',
    aedFailureHigh: 'rgb(111,22,32)',
    aedFailureStandard: 'rgb(134,39,41)',

    // Unit status colors - theme-reactive ones use CSS variables
    unitStatusAdminFill: 'var(--arc-colors-raw-gray-300)',
    unitStatusAtSceneFill: 'var(--cad-colors-unit-status-at-scene-fill)',
    unitStatusAvailableEventRelatedFill: 'var(--arc-colors-raw-blue-600)',
    unitStatusAvailableEventRelatedHeldFill: 'var(--arc-colors-raw-orange-500)',
    unitStatusAvailableFill: 'var(--arc-colors-raw-blue-800)',
    unitStatusAvailableNonEventRelatedFill: 'var(--arc-colors-raw-purple-500)',
    unitStatusDirectedPatrolFill: 'var(--arc-colors-raw-blue-900)',
    unitStatusDispatchedFill: 'var(--arc-colors-raw-yellow-800)',
    unitStatusEnRouteAlternateFill: 'var(--arc-colors-raw-red-600)',
    unitStatusEnRouteFill: 'var(--arc-colors-raw-yellow-500)',
    unitStatusHospitalTransportArrivedFill: 'var(--arc-colors-raw-green-500)',
    unitStatusHospitalTransportEnRouteFill: 'var(--arc-colors-raw-yellow-300)',
    unitStatusNoUnitStateGroupFill: 'var(--arc-colors-neutral-default)',
    unitStatusOffDutyFill: 'var(--arc-colors-neutral-default)',
    unitStatusOutOfServiceFill: 'var(--arc-colors-neutral-default)',
    unitStatusPursuitArrivedFill: 'var(--cad-colors-unit-status-pursuit-arrived-fill)',
    unitStatusPursuitEnRouteFill: 'var(--arc-colors-raw-red-700)',
    unitStatusReportWritingFill: 'var(--arc-colors-raw-purple-700)',
    unitStatusRespondingCourtArrivedFill: 'var(--arc-colors-raw-gray-500)',
    unitStatusRespondingCourtEnRouteFill: 'var(--arc-colors-raw-gray-300)',
    unitStatusServiceAssignmentFill: 'var(--arc-colors-raw-blue-200)',
    unitStatusSpecialAssignmentFill: 'var(--arc-colors-raw-blue-200)',
    unitStatusStagingFill: 'var(--arc-colors-raw-cyan-900)',
    unitStatusSystemDispatchedFill: '#523701',
    unitStatusTransportArrivedFill: 'var(--arc-colors-raw-green-500)',
    unitStatusTransportEnRouteFill: 'var(--arc-colors-raw-yellow-300)',
    unitStatusUnavailableFill: 'var(--arc-colors-raw-gray-650)',
    unitStatusUnavailableHeldFill: 'var(--arc-colors-raw-gray-300)',
    unitStatusUnitEmergencyFill: 'var(--arc-colors-raw-red-550)',
    unitStatusUnmannedFill: 'var(--arc-colors-neutral-default)',
    unitStatusVirtualPatrolFill: 'var(--arc-colors-raw-blue-200)',

    //AtScene has custom text colors
    unitStatusAtSceneText: 'var(--cad-colors-at-scene-text)',

    // Unit status text colors
    unitStatusNeutralText: 'var(--arc-colors-neutral-content)',
    unitStatusTextDark: 'var(--arc-colors-raw-black)',
    unitStatusTextLight: 'var(--arc-colors-raw-white)',
    unitStatusHeldText: 'var(--arc-colors-text-primary)',

    esriColors: {
        // AML marker colors
        amlLocationPin: [84, 163, 255], // #54A3FF

        // Polyline colors (RGBA format for Esri API)
        polylineBorder: [190, 212, 221, 0.5], // #bed4dd at 50% opacity
        polylineTracked: [0, 194, 255, 0.5], // #00C2FF at 50% opacity
        polylineDefault: [245, 245, 245, 0.5], // #F5F5F5 at 50% opacity

        // Graphics colors
        circleFill: [255, 0, 0, 0.3], // #ff0000 at 30% opacity
        circleOutline: [255, 255, 255], // #ffffff
        polygonFill: [255, 0, 0, 0.3], // #ff0000 at 30% opacity
        polygonOutline: [255, 255, 255], // #ffffff
        highlightColor: [142, 197, 255], // #8EC5FF
    }
} as const;
